/**
 * The attendance nag to-do, seen from the client.
 *
 * netlify/functions/attendanceNag.mjs writes one task per class meeting that
 * ended without an attendance mark, under the deterministic id
 * `attendanceNag_{offeringId}_{date}`. The Firestore rules own that whole
 * namespace: canUpdateTask starts with `!isAttendanceNagTask()`, so NO client
 * may write to one, a Director included (proven in the emulator for all five
 * staff roles, 2026-09-10). Only the server closes a nag: the hourly function,
 * and since 2026-10-18 the Attendance page's own call to attendanceSubmit.mjs
 * the moment the register is complete (Bobbie: "after you do add that person
 * it never goes away").
 *
 * Kim's home screen on 2026-09-10 showed the raw Firestore refusal, "Missing
 * or insufficient permissions.", after she tapped Understood/complete on
 * "Take attendance: Senior Tap 55 Plus". The button should never have been
 * offered: a nag is not something a person marks done, it is something a
 * person clears by taking attendance. Every task row that offers a Done
 * control asks this file first and, for a nag, offers Open Attendance instead,
 * deep-linked to the right class AND the right day (the Attendance page
 * defaults to today, so a next-morning mark without the date lands on the
 * wrong day and never clears the nag).
 *
 * Firebase-free on purpose so a plain node test can import it.
 */

export const ATTENDANCE_NAG_TASK_ID_PREFIX = 'attendanceNag_';

/** True for the deterministic id attendanceNag.mjs mints; false for every other task. */
export function isAttendanceNagTaskId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(ATTENDANCE_NAG_TASK_ID_PREFIX);
}

/** The fields of a task this file reads. Structural, so any task shape fits. */
export interface AttendanceNagTaskLike {
  id: string;
  attendanceNagOfferingId?: string | null;
  attendanceNagDate?: string | null;
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Which class and which day the nag is about. The task's own fields win;
 * a legacy nag written before those fields existed is parsed from its id,
 * the same regex the function's parseAttendanceNagTaskId uses, so an
 * offering id that itself contains underscores survives.
 */
export function attendanceNagTarget(
  task: AttendanceNagTaskLike
): { offeringId: string; date: string } | null {
  if (!isAttendanceNagTaskId(task.id)) return null;
  const offeringId = typeof task.attendanceNagOfferingId === 'string' ? task.attendanceNagOfferingId.trim() : '';
  const date = typeof task.attendanceNagDate === 'string' ? task.attendanceNagDate.trim() : '';
  if (offeringId && DATE_KEY.test(date)) return { offeringId, date };
  const match = /^attendanceNag_(.+)_(\d{4}-\d{2}-\d{2})$/.exec(task.id);
  if (!match) return null;
  return { offeringId: match[1], date: match[2] };
}

/**
 * The Attendance page, opened on the nag's class and day. Falls back to the
 * bare page for a nag whose target cannot be read, which is still the right
 * door: the person picks the class there.
 */
export function attendanceNagHref(task: AttendanceNagTaskLike): string {
  const target = attendanceNagTarget(task);
  if (!target) return '/attendance';
  return `/attendance?offeringId=${encodeURIComponent(target.offeringId)}&date=${encodeURIComponent(target.date)}`;
}

/** The one button a nag row offers in place of Understood/complete or Mark done. */
export const ATTENDANCE_NAG_OPEN_LABEL = 'Open Attendance';

/**
 * The one plain line under that button. Since 2026-10-18 the Attendance page
 * clears the to-do itself the moment it sees every dancer marked, and its
 * Submit button does the same (netlify/functions/attendanceSubmit.mjs); the
 * hourly function stays the net for marks saved anywhere else.
 */
export const ATTENDANCE_NAG_SELF_CLEARS = 'This clears itself once every dancer is marked. Press Submit attendance when you are done.';

/**
 * What to say if a nag write is refused anyway (a stale screen, a tap that
 * raced the rule). Never the raw Firestore string.
 */
export const ATTENDANCE_NAG_REFUSED = 'The Hub refused that. This to-do is cleared by the app itself, not by hand.';
